import { Perspectives } from "../consts";
import type {
  Perspective,
  PriceAddOn,
  PriceAddOnItemDetails,
  WireAddOnContract,
  WireAddOnMeta,
} from "../types";

type PrimaryAmountSource = {
  customer_sso_id?: string;
  primary_amount?: Record<string, number>;
  vendor_sso_id?: string;
};

type QuoteItemChildLike = {
  _id?: string;
  customer_sso_id?: string;
  item_id?: string;
  meta?: WireAddOnMeta;
  primary_amount?: Record<string, number>;
  secondary_charges?: Array<{
    _id: string;
    amount: number;
    meta?: { label?: string };
  }>;
  type: string;
  units?: number;
  vendor_sso_id?: string;
};

/**
 * Wire-boundary guard: quote children are a union of space-rental and item
 * shapes, so narrow to ITEM before reading add-on fields.
 */
const isQuoteItemChild = (child: unknown): child is QuoteItemChildLike =>
  typeof child === "object" &&
  child !== null &&
  (child as { type?: unknown }).type === "ITEM";

/**
 * Picks the perspective-appropriate primary amount from SSO-keyed amounts.
 */
const resolveAddOnAmount = (
  source: PrimaryAmountSource,
  perspective: Perspective,
): number => {
  const amounts = source.primary_amount;
  if (amounts == null) return 0;

  if (perspective === Perspectives.Guest) {
    const customerId = source.customer_sso_id;
    if (customerId != null && amounts[customerId] != null) {
      return amounts[customerId];
    }
  } else {
    const vendorId = source.vendor_sso_id;
    if (vendorId != null && amounts[vendorId] != null) {
      return amounts[vendorId];
    }
  }

  const values = Object.values(amounts);
  return values[0] ?? 0;
};

/**
 * Maps ITEM `meta` into catalog details for the add-on item-details modal.
 * Returns `undefined` when there is no photo, copy, category, or charge to show.
 */
const toPriceAddOnItemDetails = (
  meta: WireAddOnMeta | null | undefined,
): PriceAddOnItemDetails | undefined => {
  if (meta == null) return undefined;

  const additionalCharges = (meta.additional_charges ?? [])
    .filter((charge) => charge.charge_type !== "included_vat")
    .map((charge, index) => ({
      id: charge.charge_id ?? charge.label ?? `charge-${index}`,
      label: charge.label ?? "",
      amount: charge.amount ?? 0,
      unit: charge.charge_unit ?? null,
      displayString: charge.cost_display_string ?? null,
      chargeType: charge.charge_type ?? null,
    }));

  const imageId = meta.image_ids?.[0] ?? null;
  const hasDetails = Boolean(
    imageId ||
      meta.description ||
      meta.category_display_string ||
      meta.subcategory_display_string ||
      additionalCharges.length > 0,
  );

  if (!hasDetails) return undefined;

  return {
    imageId,
    categoryDisplay: meta.category_display_string ?? null,
    subcategoryDisplay: meta.subcategory_display_string ?? null,
    description: meta.description ?? null,
    unitCost: meta.unit_cost ?? null,
    unitCostBasis: meta.unit_cost_basis ?? null,
    maxCount: meta.count ?? null,
    additionalCharges,
  };
};

const toPriceAddOnFromContract = (
  contract: WireAddOnContract & PrimaryAmountSource,
  perspective: Perspective,
): PriceAddOn => ({
  id: contract._id,
  name: contract.meta?.label ?? contract.label ?? "",
  amount: resolveAddOnAmount(contract, perspective),
  quantity: contract.units,
  unitCost: contract.meta?.unit_cost ?? contract.unit_cost ?? null,
  unitCostBasis:
    contract.meta?.unit_cost_basis ?? contract.unit_cost_basis ?? null,
  secondaryCharges: (contract.secondary_charges ?? []).map((charge) => ({
    id: charge._id,
    name: charge.meta?.label ?? charge.description ?? "",
    amount: charge.amount,
  })),
  itemDetails: toPriceAddOnItemDetails(contract.meta),
});

/**
 * Maps ITEM contracts into wire-agnostic `PriceAddOn` lines, skipping included
 * FSV items and optionally deleted item ids.
 */
const mapAddOnContractsToPriceAddOns = ({
  contracts,
  deletedItemIds,
  perspective,
}: {
  contracts: WireAddOnContract[];
  deletedItemIds?: Iterable<string>;
  perspective: Perspective;
}): PriceAddOn[] | null => {
  const deleted = new Set(deletedItemIds ?? []);
  const addOns = contracts
    .filter((contract) => contract.meta?.type !== "included")
    .filter((contract) => !deleted.has(contract.item_id))
    .map((contract) =>
      toPriceAddOnFromContract(
        contract as WireAddOnContract & PrimaryAmountSource,
        perspective,
      ),
    );

  return addOns.length > 0 ? addOns : null;
};

/**
 * Maps quote ITEM children into wire-agnostic `PriceAddOn` lines.
 */
const mapQuoteItemChildrenToPriceAddOns = ({
  children,
  deletedItemIds,
  perspective,
}: {
  children: readonly unknown[];
  deletedItemIds?: Iterable<string>;
  perspective: Perspective;
}): PriceAddOn[] | null => {
  const deleted = new Set(deletedItemIds ?? []);
  const addOns = children
    .filter(isQuoteItemChild)
    .filter((child) => child.meta?.type !== "included")
    .filter((child) => child.item_id == null || !deleted.has(child.item_id))
    .map(
      (child): PriceAddOn => ({
        id: child._id ?? child.item_id ?? "",
        name: child.meta?.label ?? "",
        amount: resolveAddOnAmount(child, perspective),
        quantity: child.units,
        unitCost: child.meta?.unit_cost ?? null,
        unitCostBasis: child.meta?.unit_cost_basis ?? null,
        secondaryCharges: (child.secondary_charges ?? []).map((charge) => ({
          id: charge._id,
          name: charge.meta?.label ?? "",
          amount: charge.amount,
        })),
        itemDetails: toPriceAddOnItemDetails(child.meta),
      }),
    );

  return addOns.length > 0 ? addOns : null;
};

export {
  mapAddOnContractsToPriceAddOns,
  mapQuoteItemChildrenToPriceAddOns,
  resolveAddOnAmount,
  toPriceAddOnItemDetails,
};
